import { StyleSheet } from "@react-pdf/renderer";

const theme = {
  fonts: {
    primary: {
      light: 'Garet Light',
      medium: 'Garet Medium',
      bold: 'Garet Bold',
      fat: 'Garet Fat'
    },
    secondary: {
      regular: 'Geologica Regular',
      bold: 'Geologica Bold'
    }
  },
  fontSizes: {
    xxs: '7px',
    xs: '8px',
    sm: '9px',
    md: '11px',
    lg: '12px',
    xl: '14px',
    xxl: '15px',
    xxxl: '16px',
    huge: '18px'
  },
  colors: {
    primary: '#02ADD8',
    secondary: '#7B8991',
    tertiary: '#5E666F',
    white: '#FFFFFF',
    background: '#FAFAFA',
    yellow: '#FDC503',
    orange: '#FE9523',
    green: '#1CA37A',
    gray: '#626262'
  }
};

const baseTextStyles = {
  standard: {
    textAlign: 'left',
    letterSpacing: '0px',
  },
  label: {
    textAlign: 'left',
    letterSpacing: '0px',
    textTransform: 'uppercase',
    color: theme.colors.primary,
  },
  value: {
    textAlign: 'left',
    letterSpacing: '0px',
    color: theme.colors.secondary,
  },
};

export const PNG_L = {
  size: 540,
  pad: 18,
  col: 249,
  gap: 6,
  line: 9,
  boxPad: 5,

  photo: 249,
  gapPhoto: 5.5,
  contactBox: 199,
  gapLogo: 5,
  logoRow: 45.5,

  title: 48,
  name: 42,
  lastSeen: 40.5,
  data: 30,
  boxFisicas: 226,
  gapBox: 6,
  boxVestimenta: 93.5,
  disclaimer: 18,

  qr: 55.5,
};

PNG_L.boxWidth = PNG_L.col - 2 * PNG_L.boxPad;

// Guia de identidad, medida sobre logo-jalisco.svg con H = alto del logo de Jalisco (se conserva):
// A (escudo) = 0.6316 H, X («JALISCO») = 0.2888 A y la base de «JALISCO» queda a 0.1203 H del pie.
// COBUPEJ mide A, apoya en la base de «JALISCO» y se separa 2X; los ratios son los viewBox de cada SVG.
export const LOGOS = {
  escudo: 0.6316,
  jalisco: 0.2888,
  baseJalisco: 0.1203,
  cobupejRatio: 326.9 / 76.8,
  jalRatio: 800 / 720.96,
};

const logoGroup = (jalHeight) => {
  const a = LOGOS.escudo * jalHeight;
  return { a, gap: 2 * LOGOS.jalisco * a, base: LOGOS.baseJalisco * jalHeight };
};

PNG_L.senasSpace = PNG_L.boxFisicas - 2 * PNG_L.boxPad - 8 * PNG_L.line;
PNG_L.vestimentaSpace = PNG_L.boxVestimenta - 2 * PNG_L.boxPad - 1 * PNG_L.line;
export const PNG_SCALE = 2;
// Modal de escritorio: la cédula entera y el botón de cierre (80 + 20 px) caben en la ventana con 16 px de margen.
export const pngViewScale = () => Math.min(window.innerWidth - 32, window.innerHeight - 32 - 100) / PNG_L.size;
export const PNG_EXPORT_PX = PNG_L.size * PNG_SCALE;

export const PNG_PALETTE = {
  azul: theme.colors.primary,
  verde: '#2B8F1B',
  gris: '#333333',
  texto: '#333333',
  nombreFecha: '#002F87',
  cajaGris: '#F2F2F2',
};

export const titularFor = (condicion) => {
  if (condicion === 'CON VIDA') return { text: 'PERSONA LOCALIZADA CON VIDA', color: PNG_PALETTE.verde };
  if (condicion === 'SIN VIDA') return { text: 'PERSONA LOCALIZADA SIN VIDA', color: PNG_PALETTE.gris };
  return { text: 'AYÚDANOS A LOCALIZAR A:', color: PNG_PALETTE.azul };
};

// Ancho medio de un caracter de Montserrat Regular en mayusculas
// suma de advanceWidth / unitsPerEm / 46 = 28.590 / 46 = 0.6215.
const MONTSERRAT_CHAR_EM = 0.6215;

const pngWrapLines = (text, px, W = PNG_L.boxWidth) => {
  const unit = MONTSERRAT_CHAR_EM * px;
  let lines = 1;
  let used = 0;
  for (const word of String(text).split(/\s+/).filter(Boolean)) {
    const w = word.length * unit;
    if (used && used + unit + w > W) { lines++; used = 0; }
    used = used ? used + unit + w : w;
    if (used > W) { lines += Math.ceil(used / W) - 1; used = W; }
  }
  return lines;
};

export const pngBoxFont = (text, height, base = 7, min = 6) => {
  for (let px = base; px >= min; px -= 0.5) {
    if (pngWrapLines(text, px) * (px + 2) <= height) {
      return { fontSize: `${px}px`, lineHeight: `${px + 2}px` };
    }
  }
  return { fontSize: `${min}px`, lineHeight: `${min + 2}px` };
};

export const pngBoxClip = (height, lineHeight) => {
  const line = parseFloat(lineHeight);
  return {
    maxHeight: `${Math.floor(height / line) * line}px`,
    overflow: 'hidden',
  };
};

export const PNG_IMP_L = {
  width: 2550,
  height: 1650,
  line: 35,
  boxPad: 15,
  header: 35,
};
PNG_IMP_L.boxWidth = 1549 - 2 * PNG_IMP_L.boxPad;
PNG_IMP_L.senasBox = 466;
PNG_IMP_L.vestimentaBox = 220;
PNG_IMP_L.senasSpace = PNG_IMP_L.senasBox - 2 * PNG_IMP_L.boxPad - PNG_IMP_L.header;
PNG_IMP_L.vestimentaSpace = PNG_IMP_L.vestimentaBox - 2 * PNG_IMP_L.boxPad - PNG_IMP_L.header;

const F = 'Montserrat Cedula';
// Logos de la impresion: Jalisco de 132 con el pie en 1560, grupo centrado en la columna 60-930.
const IMP_LOGO = logoGroup(132);
const IMP_COB_W = IMP_LOGO.a * LOGOS.cobupejRatio;
const IMP_JAL_W = 132 * LOGOS.jalRatio;
const IMP_LOGO_LEFT = 60 + (870 - IMP_COB_W - IMP_LOGO.gap - IMP_JAL_W) / 2;
const abs = (left, top, width, height, extra = {}) => ({
  position: 'absolute', left, top, width, height, boxSizing: 'border-box', margin: 0, ...extra,
});

export const PNG_IMP = {
  root: {
    position: 'relative',
    width: PNG_IMP_L.width,
    height: PNG_IMP_L.height,
    background: theme.colors.white,
    overflow: 'hidden',
  },

  photoBox: abs(60, 108, 870, 870, { overflow: 'hidden', background: theme.colors.white, borderRadius: 34 }),
  personImage: { position: 'absolute', display: 'block', left: 0, top: 0, width: '100%', height: '100%' },

  contactBox: abs(60, 990, 870, 420, { background: PNG_PALETTE.azul, borderRadius: 34 }),
  contactTitle: abs(60, 990, 870, 120, {
    fontFamily: F, fontWeight: 900, fontSize: 60, lineHeight: '60px',
    color: theme.colors.white, textAlign: 'center', paddingTop: 25,
    paddingLeft: 30, paddingRight: 30,
  }),
  contactSubtitle: abs(60, 1171, 610, 35, {
    fontFamily: F, fontWeight: 700, fontSize: 35, lineHeight: '35px',
    color: theme.colors.white, textAlign: 'center',
  }),
  phoneText: abs(60, 1211, 610, 60, {
    fontFamily: F, fontWeight: 900, fontSize: 60, lineHeight: '60px',
    color: theme.colors.white, textAlign: 'center',
  }),
  emailText: abs(60, 1282, 610, 25, {
    fontFamily: F, fontWeight: 700, fontSize: 25, lineHeight: '25px',
    color: theme.colors.white, textAlign: 'center',
  }),
  redesRow: abs(60, 1337, 610, 42, {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
  }),
  redesIcon: { width: 42, height: 42, display: 'block', objectFit: 'contain' },
  redesText: {
    fontFamily: F, fontWeight: 400, fontSize: 25, lineHeight: '28px',
    color: theme.colors.white, margin: 0,
  },
  qrBox: abs(671, 1164, 225, 215, { background: theme.colors.white }),
  qrCode: { position: 'absolute', left: 24, top: 21, width: 180, height: 180 },

  logoCobupej: {
    position: 'absolute', left: IMP_LOGO_LEFT, top: 1560 - IMP_LOGO.base - IMP_LOGO.a,
    width: IMP_COB_W, height: IMP_LOGO.a, display: 'block',
  },
  logoJal: {
    position: 'absolute', left: IMP_LOGO_LEFT + IMP_COB_W + IMP_LOGO.gap, top: 1428,
    width: IMP_JAL_W, height: 132, display: 'block',
  },

  title: abs(941, 108, 1549, 108, {
    fontFamily: F, fontWeight: 900, fontSize: 81, lineHeight: '92.2px',
    textTransform: 'uppercase', paddingLeft: 10, paddingBottom: 10,
  }),
  name: abs(941, 216, 1549, 156, {
    fontFamily: F, fontWeight: 900, fontSize: 55, lineHeight: '60px',
    color: PNG_PALETTE.nombreFecha, textTransform: 'uppercase',
    padding: '10px 15px', display: 'flex', alignItems: 'center',
  }),

  fechaBox: abs(941, 372, 1549, 150, {
    padding: '10px 15px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
  }),
  // Lugar y cabello parten renglón en vez de salirse de la caja o cortarse con «…» (PDF). El cabello
  // llega a 1055 px y en ojosBox cabe un renglón más; los lugares más largos, a 1566 (caben 1519).
  fechaLine: {},
  fechaLabel: {
    fontFamily: F, fontWeight: 700, fontSize: 40, lineHeight: '45px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  },
  fechaValue: {
    fontFamily: F, fontWeight: 800, fontSize: 40, lineHeight: '45px',
    color: PNG_PALETTE.nombreFecha, textTransform: 'uppercase',
  },
  lugarLabel: {
    fontFamily: F, fontWeight: 700, fontSize: 30, lineHeight: '45px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  },
  lugarValue: {
    fontFamily: F, fontWeight: 400, fontSize: 30, lineHeight: '45px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  },

  edadBox: abs(941, 533, 768, 90, { paddingTop: 10, paddingBottom: 10, paddingLeft: 15 }),
  sexoGeneroBox: abs(1709, 533, 781, 90, { padding: '10px 15px' }),

  fisicasBox: abs(941, 636, 768, 170, {
    padding: '15px 0 15px 15px', background: PNG_PALETTE.cajaGris,
  }),
  ojosBox: abs(1709, 636, 781, 170, {
    padding: '50px 15px 15px 15px', background: PNG_PALETTE.cajaGris,
  }),
  senasBox: abs(941, 806, 1549, PNG_IMP_L.senasBox, {
    padding: 15, background: PNG_PALETTE.cajaGris, display: 'flex', flexDirection: 'column',
  }),
  vestimentaBox: abs(941, 806 + PNG_IMP_L.senasBox + 15, 1549, PNG_IMP_L.vestimentaBox, {
    padding: 15, background: PNG_PALETTE.cajaGris, display: 'flex', flexDirection: 'column',
  }),

  aviso: abs(941, 1507, 1549, 43, {
    paddingTop: 15, paddingRight: 15,
    fontFamily: F, fontWeight: 400, fontSize: 24, lineHeight: '28px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  }),

  dataRow: {},
  dataLabel: {
    fontFamily: F, fontWeight: 700, fontSize: 30, lineHeight: '35px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  },
  dataValue: {
    fontFamily: F, fontWeight: 400, fontSize: 30, lineHeight: '35px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase',
  },
  boxHeader: {
    fontFamily: F, fontWeight: 700, fontSize: 35, lineHeight: '35px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase', margin: 0, whiteSpace: 'nowrap',
  },
  boxText: {
    fontFamily: F, fontWeight: 400, fontSize: 25, lineHeight: '30px',
    color: PNG_PALETTE.texto, textTransform: 'uppercase', margin: 0,
    overflow: 'visible', overflowWrap: 'break-word', flexShrink: 0,
  },
  // El espacio bajo el título se reduce antes de quitarle sitio al texto.
  boxGap: { flex: '0 1 10px' },
};

const PRINT_DPI = 300;
export const PT_PER_PX = 72 / PRINT_DPI;
export const PDF_PAGE = [PNG_IMP_L.width * PT_PER_PX, PNG_IMP_L.height * PT_PER_PX];

// En CSS el texto desborda su caja (overflow visible); react-pdf encoge los hijos hasta el alto del
// padre y DESCARTA las lineas que no caben, incluso las que empatan al punto (subtitulo 35/35,
// telefono 60/60). Por eso nada encoge, y el alto solo lo conservan las cajas absolutas que no son
// texto suelto: las de texto (fontSize sin flex) miden lo que su texto, como en CSS.
const keepsHeight = (css) => css.position === 'absolute' && !(css.fontSize && !css.display);

export const toPdfStyle = (css) => Object.entries(css).reduce((out, [key, v]) => {
  if (key === 'height' && !keepsHeight(css)) return out;
  if (key === 'background') out.backgroundColor = v;
  // react-pdf multiplica lineHeight por fontSize: es un RATIO. En puntos el texto desaparecia.
  else if (key === 'lineHeight') out.lineHeight = parseFloat(v) / css.fontSize;
  else if (key === 'padding') {
    const [t, r = t, b = t, l = r] = String(v).split(' ').map(parseFloat);
    Object.assign(out, { paddingTop: t * PT_PER_PX, paddingRight: r * PT_PER_PX, paddingBottom: b * PT_PER_PX, paddingLeft: l * PT_PER_PX });
  }
  else if (key === 'gap') out.columnGap = out.rowGap = v * PT_PER_PX;
  else if (key === 'whiteSpace') { if (v === 'nowrap') Object.assign(out, { maxLines: 1, textOverflow: 'ellipsis' }); }
  // En CSS un flex sin direccion es fila; en react-pdf toda View es columna.
  else if (key === 'display') { if (v === 'flex' && !css.flexDirection) out.flexDirection = 'row'; }
  else if (key === 'boxSizing' || key === 'overflowWrap' || v === 'auto' || v === 'visible') { }
  else out[key] = typeof v === 'number' && key !== 'fontWeight' ? v * PT_PER_PX : v;
  return out;
}, { flexShrink: 0 });

const PDF_SHEET = {
  ...Object.fromEntries(Object.entries(PNG_IMP).map(([key, css]) => [key, toPdfStyle(css)])),
  // La foto llega ya estirada al recuadro (toPrintPhoto); fill tambien estira la imagen de respaldo.
  personImage: { width: '100%', height: '100%', objectFit: 'fill' },
  qrImage: { width: '100%', height: '100%' },
  text: { flexShrink: 0 },
  boxGap: { flexGrow: 0, flexShrink: 1, flexBasis: 10 * PT_PER_PX },
};

const pngCqw = (pngPx) => `${+(pngPx / PNG_L.size * 100).toFixed(2)}cqw`;
const cqw = (n) => `${+n.toFixed(2)}cqw`;
const WEB_LOGOS = logoGroup(8.4);
const PNG_LOGOS = logoGroup(PNG_L.logoRow);

const styles = {
  WEB: {
    tableStyles: {
      position: 'relative',
      width: '100%',
      boxShadow: '0px 5px 24px #4A4B4B1A',
      borderRadius: '25px',
      paddingBottom: '10px',
      '@media (maxWidth: 767px)': {
        width: '95% !important',
        margin: 'auto',
      },
    },
    mainContainer: {
      position: 'relative',
      containerType: 'inline-size',
      padding: { xs: 1, sm: 1.25 },
      marginTop: 1,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'box-shadow 0.3s ease',
      '&:has(button:focus-visible)': { outline: `2px solid ${PNG_PALETTE.nombreFecha}`, outlineOffset: '2px' },
    },
    mainContainerHovered: {
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    },
    mainContainerNormal: {
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      borderRadius: 0,
    },
    watermark: {
      width: '62%',
      maxWidth: '330px',
      position: 'absolute',
      inset: 0,
      margin: 'auto',
      zIndex: 0,
      opacity: 0.06,
      pointerEvents: 'none',
    },
    actionContainer: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      margin: '0 auto',
    },
    actionItem: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: { xs: '100%', sm: 'auto' },
    },
    actionImg: {
      width: '34px',
      height: '34px',
      marginRight: '4px',
    },
    actionText: {
      ...baseTextStyles.standard,
      color: theme.colors.secondary,
      fontFamily: `${theme.fonts.secondary.regular} !important`,
      fontSize: theme.fontSizes.lg,
      opacity: 1,
      textTransform: 'capitalize',
    },
    modalContent: {
      boxShadow: 'none',
      border: 'none',
      borderColor: 'transparent',
      padding: 0,
      margin: 0,
    },
    modalFit: { background: 'transparent', paddingBottom: 100 },
    closeIcon: {
      position: 'absolute',
      left: '50%',
      width: 80,
      height: 80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: 'translateX(-50%)',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
      backgroundColor: '#e9edef',
      border: 'none',
    },
    closeIconButton: {
      color: '#7c8891',
      fontSize: '24px',
    },
    body: {
      position: 'relative',
      zIndex: 1,
      fontFamily: "'Montserrat Cedula', system-ui, sans-serif",
      color: PNG_PALETTE.gris,
    },
    lbl: { fontWeight: 700, textTransform: 'uppercase' },
    val: { fontWeight: 400, textTransform: 'uppercase' },
    titular: { fontWeight: 900, textTransform: 'uppercase', lineHeight: 1.05 },
    nombre: { fontWeight: 900, color: PNG_PALETTE.nombreFecha, textTransform: 'uppercase', lineHeight: 1.1 },
    fecha: { fontWeight: 800, color: PNG_PALETTE.nombreFecha, textTransform: 'uppercase' },
    whole: { display: 'inline-block' },
    desktop: {
      root: {
        containerType: 'inline-size',
        aspectRatio: '1',
        display: 'grid',
        gridTemplateColumns: '46.1% 1fr',
        gap: '1.1cqw',
      },
      open: {
        position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: '0',
      },
      left: { display: 'flex', flexDirection: 'column', gap: '1cqw', minHeight: 0 },
      photo: { width: '100%', aspectRatio: '1', overflow: 'hidden', borderRadius: '3.1cqw', flexShrink: 0 },
      contact: {
        flex: 1, minHeight: 0, background: PNG_PALETTE.azul, color: theme.colors.white, borderRadius: '3.1cqw',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: `${pngCqw(9)} ${pngCqw(30)}`, overflow: 'hidden',
      },
      t: { fontWeight: 700, fontSize: pngCqw(14 * 1.1), lineHeight: 0.964 },
      s: { fontSize: pngCqw(10 * 1.1), lineHeight: 1.2, marginTop: pngCqw(2) },
      ph: { fontWeight: 900, fontSize: pngCqw(20 * 1.1), lineHeight: 1.2, marginTop: pngCqw(5) },
      em: { fontSize: pngCqw(9 * 1.1), lineHeight: 1.22, marginTop: pngCqw(2), overflowWrap: 'anywhere' },
      qrbox: {
        width: pngCqw(PNG_L.qr * 1.1), aspectRatio: '1', marginTop: pngCqw(5), background: theme.colors.white,
        padding: pngCqw(3), boxSizing: 'border-box',
        '& .ant-qrcode': {
          width: '100% !important', height: '100% !important', padding: 0, border: '0', borderRadius: 0,
          boxSizing: 'border-box',
        },
        '& svg': { width: '100% !important', height: '100% !important', display: 'block' },
      },
      redes: {
        display: 'flex', alignItems: 'center', gap: pngCqw(4), fontSize: pngCqw(7.5 * 1.1), lineHeight: 1.2, marginTop: pngCqw(10),
        '& img': { width: pngCqw(15 * 1.1), height: pngCqw(15 * 1.1), background: PNG_PALETTE.azul },
      },
      logos: {
        display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: cqw(WEB_LOGOS.gap), height: '8.4cqw',
        '& img': { height: '100%', width: 'auto', objectFit: 'contain' },
        '& img:first-of-type': { height: cqw(WEB_LOGOS.a), marginBottom: cqw(WEB_LOGOS.base) },
      },
      right: { display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 },
      titular: { fontSize: 'max(14px, 3.5cqw)' },
      nombre: { fontSize: 'max(11px, 2.2cqw)', margin: '1.2cqw 0' },
      ls: { fontSize: 'max(9px, 1.48cqw)', lineHeight: 1.25 },
      dl: { fontSize: 'max(9px, 1.3cqw)', lineHeight: 1.25, marginBottom: '1cqw' },
      box: {
        background: PNG_PALETTE.cajaGris, padding: '1cqw', fontSize: 'max(9px, 1.3cqw)',
        lineHeight: 1.25, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column',
        '&::after': {
          content: "''", position: 'absolute', inset: 'auto 0 0', height: '14px',
          background: `linear-gradient(${PNG_PALETTE.cajaGris}00, ${PNG_PALETTE.cajaGris})`, pointerEvents: 'none',
        },
      },
      boxF: { flex: 1, minHeight: 0 },
      boxV: { flex: '0 0 17%', marginTop: '1.1cqw' },
      spacer: { height: 'max(6px, 1cqw)', flexShrink: 0 },
      gap: { flex: '0 1 .4em' },
      disc: { fontSize: 'max(7px, 1.1cqw)', textTransform: 'uppercase', marginTop: '.6cqw' },
    },
    mobile: {
      root: { maxWidth: '460px', marginInline: 'auto' },
      band: { color: theme.colors.white, padding: '8px 12px', borderRadius: '10px', fontSize: '18px' },
      hero: { marginTop: '10px' },
      photo: { width: '100%', aspectRatio: '4 / 3', overflow: 'hidden', borderRadius: '14px' },
      nombre: { fontSize: '17px', margin: '12px 2px 4px' },
      ls: { fontSize: '11px', margin: '0 2px', lineHeight: 1.3 },
      chips: { display: 'flex', flexWrap: 'wrap', gap: '5px', margin: '10px 0', alignContent: 'flex-start' },
      chip: {
        background: PNG_PALETTE.cajaGris, borderRadius: '20px', padding: '3px 9px', fontSize: '10.5px',
        textTransform: 'uppercase',
      },
      datos: {
        position: 'relative', background: PNG_PALETTE.cajaGris, borderRadius: '12px', padding: '10px 12px',
        fontSize: '11px', lineHeight: 1.4,
      },
      lbl2: { display: 'block', marginTop: '6px' },
      clamp2: {
        display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical',
        overflow: 'hidden', minHeight: '2.8em', paddingRight: '24px',
      },
      expand: { position: 'absolute', right: '4px', bottom: '4px' },
      contact: {
        marginTop: '10px', background: PNG_PALETTE.azul, color: theme.colors.white, borderRadius: '14px',
        padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
      },
      t: { fontWeight: 700, fontSize: '12px' },
      ph: { fontWeight: 900, fontSize: '20px' },
      em: { fontSize: '10px', overflowWrap: 'anywhere' },
    },
  },
  PNG: {
    cardWrapper: {
      width: PNG_L.size,
      height: PNG_L.size,
      boxShadow: 'none',
      border: 'none',
      borderRadius: 10,
      background: theme.colors.white,
      overflow: 'hidden',
      position: 'relative',
    },
    cardBody: {
      width: '100%',
      height: '100%',
      padding: PNG_L.pad,
      boxSizing: 'border-box',
      position: 'relative',
    },
    watermark: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      opacity: 0.06,
      pointerEvents: 'none',
    },

    row: {
      display: 'flex',
      gap: PNG_L.gap,
      height: '100%',
      position: 'relative',
    },
    colLeft: {
      flex: `0 0 ${PNG_L.col}px`,
      display: 'flex',
      flexDirection: 'column',
    },
    colRight: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },

    photoBox: {
      flex: `0 0 ${PNG_L.photo}px`,
      width: PNG_L.photo,
      height: PNG_L.photo,
      overflow: 'hidden',
      background: theme.colors.background,
      borderRadius: 17,
    },
    personImage: {
      width: '100%',
      height: '100%',
      objectFit: 'fill',
      display: 'block',
    },
    skeletonImage: {
      width: '100%',
      height: '100%',
    },

    contactBox: {
      flex: `0 0 ${PNG_L.contactBox}px`,
      marginTop: PNG_L.gapPhoto,
      background: PNG_PALETTE.azul,
      color: theme.colors.white,
      borderRadius: 17,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '9px 30px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    contactTitle: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '13.5px',
      color: theme.colors.white,
      textAlign: 'center',
      margin: 0,
    },
    contactSubtitle: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '10px',
      lineHeight: '12px',
      color: theme.colors.white,
      textAlign: 'center',
      margin: '2px 0 0 0',
    },
    phoneText: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 900,
      fontSize: '20px',
      lineHeight: '24px',
      color: theme.colors.white,
      textAlign: 'center',
      margin: '5px 0 0 0',
    },
    emailText: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '9px',
      lineHeight: '11px',
      color: theme.colors.white,
      textAlign: 'center',
      margin: '2px 0 0 0',
    },
    qrWrapper: {
      width: PNG_L.qr,
      height: PNG_L.qr,
      background: theme.colors.white,
      padding: 3,
      boxSizing: 'border-box',
      margin: '5px 0 0 0',
    },
    qrCode: {
      width: '100%',
      height: '100%',
    },
    redesRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      margin: '10px 0 0 0',
    },
    redesIcon: {
      width: 15,
      height: 15,
      display: 'block',
      objectFit: 'contain',
      backgroundColor: theme.colors.primary,
    },
    redesText: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '7.5px',
      lineHeight: '9px',
      color: theme.colors.white,
      margin: 0,
    },

    logoRow: {
      flex: `0 0 ${PNG_L.logoRow}px`,
      marginTop: PNG_L.gapLogo,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: PNG_LOGOS.gap,
    },
    cobupejLogo: {
      height: PNG_LOGOS.a,
      marginBottom: PNG_LOGOS.base,
      width: 'auto',
      objectFit: 'contain',
    },
    logoJal: {
      height: PNG_L.logoRow,
      width: 'auto',
      objectFit: 'contain',
    },

    title: {
      flex: `0 0 ${PNG_L.title}px`,
      fontFamily: 'Montserrat Cedula',
      fontWeight: 900,
      fontSize: '19px',
      lineHeight: '22.8px',
      textTransform: 'uppercase',
      margin: 0,
      overflow: 'hidden',
    },
    name: {
      flex: `0 0 ${PNG_L.name}px`,
      display: 'flex',
      alignItems: 'center',
      fontFamily: 'Montserrat Cedula',
      fontWeight: 900,
      fontSize: '12px',
      lineHeight: '12.5px',
      color: PNG_PALETTE.nombreFecha,
      textTransform: 'uppercase',
      margin: 0,
      overflow: 'hidden',
    },

    // Reserva cuatro renglones: etiqueta, fecha y hasta dos de ubicación.
    lastSeen: {
      flex: `0 0 ${PNG_L.lastSeen}px`,
      overflow: 'hidden',
    },
    lastSeenLabel: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 700,
      fontSize: '8px',
      lineHeight: `${PNG_L.lastSeen / 4}px`,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      margin: 0,
    },
    lastSeenDate: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 800,
      fontSize: '8px',
      lineHeight: `${PNG_L.lastSeen / 4}px`,
      color: PNG_PALETTE.nombreFecha,
      textTransform: 'uppercase',
      margin: 0,
    },
    lastSeenPlace: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '8px',
      lineHeight: `${PNG_L.lastSeen / 4}px`,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      margin: 0,
    },

    dataList: {
      flex: `0 0 ${PNG_L.data}px`,
      overflow: 'hidden',
    },
    dataRow: {
      display: 'flex',
      alignItems: 'baseline',
      height: PNG_L.line,
    },
    dataLabel: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 700,
      fontSize: '7px',
      lineHeight: `${PNG_L.line}px`,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      margin: 0,
    },
    dataValue: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '7px',
      lineHeight: `${PNG_L.line}px`,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      margin: 0,
    },

    boxFisicas: {
      flex: `0 0 ${PNG_L.boxFisicas}px`,
      background: PNG_PALETTE.cajaGris,
      padding: PNG_L.boxPad,
      boxSizing: 'border-box',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    boxVestimenta: {
      flex: `0 0 ${PNG_L.boxVestimenta}px`,
      marginTop: PNG_L.gapBox,
      background: PNG_PALETTE.cajaGris,
      padding: PNG_L.boxPad,
      boxSizing: 'border-box',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    boxLabel: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 700,
      fontSize: '7px',
      lineHeight: `${PNG_L.line}px`,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      margin: 0,
    },
    boxText: {
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      margin: 0,
      overflowWrap: 'break-word',
      flexShrink: 0,
    },
    boxSpacer: {
      height: PNG_L.line,
      flexShrink: 0,
    },
    boxGap: {
      flex: '0 1 3px',
    },

    disclaimer: {
      flex: `0 0 ${PNG_L.disclaimer}px`,
      fontFamily: 'Montserrat Cedula',
      fontWeight: 400,
      fontSize: '6px',
      lineHeight: '7.5px',
      color: PNG_PALETTE.texto,
      textTransform: 'uppercase',
      margin: 0,
      overflow: 'hidden',
    },
  },

  PDF: StyleSheet.create(PDF_SHEET),
};
export default styles;
export const WEB = styles.WEB;
export const PNG = styles.PNG;
export const PDF = styles.PDF;

export const updateFonts = (primaryFont, secondaryFont) => {
  theme.fonts.primary = {
    light: `${primaryFont} Light`,
    medium: `${primaryFont} Medium`,
    bold: `${primaryFont} Bold`,
    fat: `${primaryFont} Fat`
  };
  
  theme.fonts.secondary = {
    regular: `${secondaryFont} Regular`,
    bold: `${secondaryFont} Bold`
  };
  
  return theme.fonts;
};
